class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems =
        this.closest('cart-items') ||
        this.closest('cart-drawer-items');

      if (!cartItems) return;

      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}


class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce(
      (event) => this.onChange(event),
      ON_CHANGE_DEBOUNCE_TIMER
    );

    this.addEventListener('change', debouncedOnChange);
  }


  cartUpdateUnsubscriber = undefined;


  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        /*
         * Do not refresh twice when this component
         * itself triggered the update.
         */
        if (event.source === 'cart-items') return;

        this.refreshCart();
      }
    );
  }


  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }


  onChange(event) {
    const input = event.target;

    if (!input.dataset.index) return;

    this.updateQuantity(
      input.dataset.index,
      input.value,
      document.activeElement
        ? document.activeElement.getAttribute('name')
        : null
    );
  }


  /*
   * ------------------------------------------
   * CART SECTION LIST
   * ------------------------------------------
   */

  getSectionsToRender() {
    const mainCartItems =
      document.getElementById('main-cart-items');

    const mainCartFooter =
      document.getElementById('main-cart-footer');

    const sections = [];

    if (mainCartItems) {
      sections.push({
        id: 'main-cart-items',
        section:
          mainCartItems.dataset.id ||
          'main-cart-items',
        selector: '#main-cart-items'
      });
    }

    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section:
          mainCartFooter.dataset.id ||
          'main-cart-footer',
        selector: '#main-cart-footer'
      });
    }

    const cartIconBubble =
      document.getElementById('cart-icon-bubble');

    if (cartIconBubble) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble'
      });
    }

    return sections;
  }


  /*
   * ------------------------------------------
   * REFRESH CART
   * ------------------------------------------
   */

  refreshCart() {
    const sections = this.getSectionsToRender();

    if (!sections.length) return;

    const sectionIds = sections
      .map((section) => section.section)
      .join(',');

    fetch(
      `${window.location.pathname}?sections=${sectionIds}`,
      {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      }
    )
      .then((response) => response.json())
      .then((sectionsHTML) => {
        this.renderSections(sectionsHTML);
      })
      .catch((error) => {
        console.error(
          'CrazyFlora cart refresh error:',
          error
        );
      });
  }


  /*
   * ------------------------------------------
   * UPDATE CART QUANTITY
   * ------------------------------------------
   */

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const sections = this
      .getSectionsToRender()
      .map((section) => section.section);

    const body = JSON.stringify({
      line: Number(line),
      quantity: Number(quantity),
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(
      `${routes.cart_change_url}`,
      {
        ...fetchConfig(),
        body: body
      }
    )
      .then((response) => response.json())
      .then((parsedState) => {

        /*
         * --------------------------------------
         * ERROR
         * --------------------------------------
         */

        if (parsedState.errors) {

          const quantityElement =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);

          if (quantityElement) {
            quantityElement.value =
              quantityElement.getAttribute('value');
          }

          this.updateLiveRegions(
            line,
            parsedState.errors
          );

          return;
        }


        /*
         * --------------------------------------
         * EMPTY STATE
         * --------------------------------------
         */

        this.classList.toggle(
          'is-empty',
          parsedState.item_count === 0
        );

        const cartFooter =
          document.getElementById('main-cart-footer');

        if (cartFooter) {
          cartFooter.classList.toggle(
            'is-empty',
            parsedState.item_count === 0
          );
        }

        const cartDrawer =
          document.querySelector('cart-drawer');

        if (cartDrawer) {
          cartDrawer.classList.toggle(
            'is-empty',
            parsedState.item_count === 0
          );
        }


        /*
         * --------------------------------------
         * RENDER UPDATED SECTIONS
         * --------------------------------------
         */

        this.renderSections(
          parsedState.sections
        );


        /*
         * --------------------------------------
         * QUANTITY VALIDATION
         * --------------------------------------
         */

        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);

        const updatedValue =
          parsedState.items &&
          parsedState.items[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;

        let message = '';

        const items =
          document.querySelectorAll('.cart-item');

        if (
          quantityElement &&
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value, 10)
        ) {

          if (typeof updatedValue === 'undefined') {

            message =
              window.cartStrings.error;

          } else {

            message =
              window.cartStrings.quantityError.replace(
                '[quantity]',
                updatedValue
              );

          }
        }

        this.updateLiveRegions(
          line,
          message
        );


        /*
         * --------------------------------------
         * RESTORE FOCUS
         * --------------------------------------
         */

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);

        if (
          lineItem &&
          name &&
          lineItem.querySelector(`[name="${name}"]`)
        ) {

          const target =
            lineItem.querySelector(`[name="${name}"]`);

          const drawer =
            document.querySelector('cart-drawer');

          if (drawer) {
            trapFocus(drawer, target);
          } else {
            target.focus();
          }

        } else if (
          parsedState.item_count === 0 &&
          cartDrawer
        ) {

          const emptyDrawer =
            cartDrawer.querySelector(
              '.drawer__inner-empty'
            );

          if (emptyDrawer) {
            trapFocus(
              cartDrawer,
              emptyDrawer.querySelector('a')
            );
          }

        }


        /*
         * --------------------------------------
         * TELL THEME CART HAS CHANGED
         * --------------------------------------
         */

        publish(
          PUB_SUB_EVENTS.cartUpdate,
          {
            source: 'cart-items',
            cart: parsedState
          }
        );
      })
      .catch((error) => {

        console.error(
          'CrazyFlora cart update error:',
          error
        );

        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');

        if (errors) {
          errors.textContent =
            window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }


  /*
   * ------------------------------------------
   * RENDER SECTIONS
   * ------------------------------------------
   */

  renderSections(sections) {
    if (!sections) return;

    const sectionsToRender =
      this.getSectionsToRender();

    sectionsToRender.forEach((section) => {

      const html =
        sections[section.section];

      if (!html) return;

      const parsed =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );


      /*
       * --------------------------------------
       * CART ITEMS
       * --------------------------------------
       */

      if (section.id === 'main-cart-items') {

        const current =
          document.getElementById(
            'main-cart-items'
          );

        const updated =
          parsed.querySelector(
                '#main-cart-items'
              );

        if (current && updated) {

          current.innerHTML =
            updated.innerHTML;
        }

        return;
      }


      /*
       * --------------------------------------
       * CART FOOTER
       *
       * IMPORTANT:
       * Keep the existing outer element.
       * Only replace its INNER CONTENT.
       * --------------------------------------
       */

      if (section.id === 'main-cart-footer') {

        const current =
          document.getElementById(
            'main-cart-footer'
          );

        const updated =
          parsed.querySelector(
            '#main-cart-footer'
          );

        if (current && updated) {

          /*
           * Preserve:
           *
           * #main-cart-footer
           *
           * including:
           * data-id
           * classes
           *
           * Replace ONLY:
           *
           * innerHTML
           */

          current.innerHTML =
            updated.innerHTML;

        }

        return;
      }


      /*
       * --------------------------------------
       * CART DRAWER (mini-cart)
       *
       * <cart-drawer-items> (assets/cart-drawer.js)
       * extends this class and overrides
       * getSectionsToRender() to ask for a
       * section it calls "CartDrawer", rendered
       * into ".drawer__inner". But it never
       * overrides renderSections() itself, so
       * without this branch that id never matches
       * anything below and the drawer's HTML is
       * silently discarded: the real cart update
       * still succeeds on Shopify's end, but the
       * drawer keeps showing whatever it had
       * before, out of sync with the true cart.
       * --------------------------------------
       */

      if (section.id === 'CartDrawer') {

        const current =
          document.querySelector(
            '.drawer__inner'
          );

        const updated =
          parsed.querySelector(
            '.drawer__inner'
          );

        if (current && updated) {
          current.innerHTML =
            updated.innerHTML;
        }

        return;
      }


      /*
       * --------------------------------------
       * CART ICON
       * --------------------------------------
       */

      if (section.id === 'cart-icon-bubble') {

        const current =
          document.getElementById(
            'cart-icon-bubble'
          );

        const updated =
          parsed.querySelector(
            '#cart-icon-bubble'
          );

        if (current && updated) {
          current.innerHTML =
            updated.innerHTML;
        }

        return;
      }
    });
  }


  /*
   * ------------------------------------------
   * ACCESSIBILITY
   * ------------------------------------------
   */

  updateLiveRegions(line, message) {

    const lineItemError =
      document.getElementById(
        `Line-item-error-${line}`
      ) ||
      document.getElementById(
        `CartDrawer-LineItemError-${line}`
      );

    if (lineItemError) {

      const errorText =
        lineItemError.querySelector(
          '.cart-item__error-text'
        );

      if (errorText) {
        errorText.innerHTML =
          message;
      }
    }


    if (this.lineItemStatusElement) {

      this.lineItemStatusElement.setAttribute(
        'aria-hidden',
        true
      );
    }


    const cartStatus =
      document.getElementById(
        'cart-live-region-text'
      ) ||
      document.getElementById(
        'CartDrawer-LiveRegionText'
      );

    if (cartStatus) {

      cartStatus.setAttribute(
        'aria-hidden',
        false
      );

      setTimeout(() => {

        cartStatus.setAttribute(
          'aria-hidden',
          true
        );

      }, 1000);
    }
  }


  /*
   * ------------------------------------------
   * LOADING
   * ------------------------------------------
   */

  enableLoading(line) {

    const mainCartItems =
      document.getElementById(
        'main-cart-items'
      ) ||
      document.getElementById(
        'CartDrawer-CartItems'
      );

    if (mainCartItems) {
      mainCartItems.classList.add(
        'cart__items--disabled'
      );
    }


    const itemElements =
      this.querySelectorAll(
        `#CartItem-${line} .loading-overlay`
      );

    const drawerElements =
      this.querySelectorAll(
        `#CartDrawer-Item-${line} .loading-overlay`
      );


    [
      ...itemElements,
      ...drawerElements
    ].forEach((overlay) => {
      overlay.classList.remove('hidden');
    });


    if (
      document.activeElement &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur();
    }


    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute(
        'aria-hidden',
        false
      );
    }
  }


  disableLoading(line) {

    const mainCartItems =
      document.getElementById(
        'main-cart-items'
      ) ||
      document.getElementById(
        'CartDrawer-CartItems'
      );

    if (mainCartItems) {
      mainCartItems.classList.remove(
        'cart__items--disabled'
      );
    }


    const itemElements =
      this.querySelectorAll(
        `#CartItem-${line} .loading-overlay`
      );

    const drawerElements =
      this.querySelectorAll(
        `#CartDrawer-Item-${line} .loading-overlay`
      );


    [
      ...itemElements,
      ...drawerElements
    ].forEach((overlay) => {
      overlay.classList.add('hidden');
    });
  }
}


if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    CartItems
  );
}


/*
 * ------------------------------------------
 * CART NOTE
 * ------------------------------------------
 */

if (!customElements.get('cart-note')) {

  customElements.define(
    'cart-note',

    class CartNote extends HTMLElement {

      constructor() {

        super();

        this.addEventListener(
          'change',

          debounce(
            (event) => {

              const body =
                JSON.stringify({
                  note: event.target.value
                });

              fetch(
                `${routes.cart_update_url}`,
                {
                  ...fetchConfig(),
                  body: body
                }
              );

            },
            ON_CHANGE_DEBOUNCE_TIMER
          )
        );
      }
    }
  );
}